"use client";

import { useCallback, useEffect, useState } from "react";
import { AdminAuthError, adminFetch } from "@/lib/admin-client";
import { AdminKeyGate } from "@/components/admin-key-gate";
import { SchemaEditor } from "@/components/schema-editor";
import { Badge, Button, Card, Spinner, inputClass } from "@/components/ui";

interface RegVoice {
  id: string;
  name: string;
  locale: string;
  gender: "female" | "male";
  speed: number;
  enabled: boolean;
  externalId?: string;
}

export default function AdminVoicesPage() {
  const [items, setItems] = useState<RegVoice[] | null>(null);
  const [needKey, setNeedKey] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    locale: "en-US",
    gender: "female",
    speed: "1",
    externalId: "",
  });

  const load = useCallback(async () => {
    try {
      setItems(await adminFetch<RegVoice[]>("/api/admin/assets/voices"));
      setNeedKey(false);
    } catch (e) {
      if (e instanceof AdminAuthError) setNeedKey(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function create() {
    setBusy("create");
    setNotice(null);
    try {
      await adminFetch("/api/admin/assets/voices", {
        method: "POST",
        body: JSON.stringify({ ...form, speed: Number(form.speed) || 1 }),
      });
      setForm({ ...form, name: "", externalId: "" });
      setNotice("Voice added — live in the wizard now");
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Create failed");
    } finally {
      setBusy(null);
    }
  }

  async function toggle(v: RegVoice) {
    setBusy(v.id);
    try {
      await adminFetch(`/api/admin/assets/voices?id=${v.id}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: !v.enabled }),
      });
      await load();
    } finally {
      setBusy(null);
    }
  }

  async function bind(v: RegVoice) {
    const externalId = window.prompt(
      `HeyGen voice_id for ${v.name}`,
      v.externalId ?? "",
    );
    if (externalId === null) return;
    setBusy(v.id);
    try {
      await adminFetch(`/api/admin/assets/voices?id=${v.id}`, {
        method: "PATCH",
        body: JSON.stringify({ externalId }),
      });
      await load();
    } finally {
      setBusy(null);
    }
  }

  async function remove(v: RegVoice) {
    if (!window.confirm(`Delete voice "${v.name}"?`)) return;
    setBusy(v.id);
    try {
      await adminFetch(`/api/admin/assets/voices?id=${v.id}`, {
        method: "DELETE",
      });
      await load();
    } finally {
      setBusy(null);
    }
  }

  if (needKey) return <AdminKeyGate onUnlock={() => void load()} />;
  if (!items) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-muted">
        <Spinner className="size-6" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-5 py-8 sm:py-10">
      <h1 className="text-2xl font-semibold tracking-tight">
        Voice container
      </h1>
      <p className="mt-2 text-sm text-muted">
        The container defines the recording format for voice assets across
        tech routes. Below it: the platform's preset voices, live in the
        wizard.
      </p>

      <SchemaEditor kind="voice" />

      {notice && (
        <Card className="mt-4 px-4 py-3 text-[13px] text-muted">{notice}</Card>
      )}

      <h2 className="mt-10 mb-3 text-sm font-medium text-muted">
        Platform preset voices
      </h2>

      <Card className="p-5">
        <div className="mb-3 text-sm font-medium">New voice</div>
        <div className="grid gap-3 sm:grid-cols-3">
          <input
            className={inputClass}
            placeholder='Name, e.g. "Maya — Calm"'
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <select
            className={inputClass}
            value={form.gender}
            onChange={(e) => setForm({ ...form, gender: e.target.value })}
          >
            <option value="female">Female</option>
            <option value="male">Male</option>
          </select>
          <input
            className={inputClass}
            placeholder="Locale, e.g. en-US"
            value={form.locale}
            onChange={(e) => setForm({ ...form, locale: e.target.value })}
          />
          <input
            className={inputClass}
            placeholder="Speed, e.g. 1 / 1.15"
            value={form.speed}
            onChange={(e) => setForm({ ...form, speed: e.target.value })}
          />
          <input
            className={`${inputClass} sm:col-span-2`}
            placeholder="External ID (HeyGen voice_id)"
            value={form.externalId}
            onChange={(e) => setForm({ ...form, externalId: e.target.value })}
          />
        </div>
        <Button
          className="mt-4"
          size="sm"
          disabled={busy === "create" || !form.name}
          onClick={() => void create()}
        >
          {busy === "create" ? <Spinner /> : "Add voice"}
        </Button>
      </Card>

      <Card className="mt-6 divide-y divide-[var(--border)] p-0">
        {items.map((v) => (
          <div key={v.id} className="flex items-center gap-4 px-5 py-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{v.name}</span>
                <Badge tone={v.enabled ? "success" : "neutral"}>
                  {v.enabled ? "live" : "disabled"}
                </Badge>
              </div>
              <div className="mt-1 text-[12px] text-muted">
                {v.gender} · {v.locale} · {v.speed}x ·{" "}
                <span className="font-mono">
                  ext: {v.externalId ?? "— not bound —"}
                </span>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy === v.id}
              onClick={() => void toggle(v)}
            >
              {v.enabled ? "Disable" : "Enable"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy === v.id}
              onClick={() => void bind(v)}
            >
              Bind ID
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="text-[var(--danger)]"
              disabled={busy === v.id}
              onClick={() => void remove(v)}
            >
              Delete
            </Button>
          </div>
        ))}
      </Card>
    </div>
  );
}
